import { context, defaultTextMapSetter, propagation, ROOT_CONTEXT, trace, TraceFlags, } from '@opentelemetry/api';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { FORGE_REQUEST_TRANSACTION } from './metrics.constants';
import { flushWebTracedSpan, getParentSpan, getWebTracer } from './trace';
let sessionContext = context.active();
export function useSpanContext({ tracerName, spanArgs }) {
    const tracer = getWebTracer(tracerName);
    if (!tracer) {
        const error = new Error('useSpan depends on an OtelTracer');
        throw error;
    }
    const parentSpan = getParentSpan();
    const traceFlags = TraceFlags.SAMPLED;
    const carrier = {};
    const spanContext = {
        spanId: '',
        traceFlags,
        traceId: '',
    };
    const spanOptions = Object.assign({}, FORGE_REQUEST_TRANSACTION);
    const currContext = context.active();
    if ((spanArgs === null || spanArgs === void 0 ? void 0 : spanArgs.length) === 3) {
        Object.assign(currContext, spanArgs.pop());
    }
    if (parentSpan) {
        propagation.extract(context.active(), carrier);
        Object.assign(currContext, parentSpan.spanContext());
        Object.assign(spanContext, parentSpan);
    }
    if ((spanArgs === null || spanArgs === void 0 ? void 0 : spanArgs.length) === 2) {
        Object.assign(spanOptions, spanArgs.pop());
    }
    const link = {
        context: spanContext,
    };
    Object.assign(spanOptions, { links: [link] });
    const span = spanArgs
        ? tracer.startSpan(spanArgs[0], spanOptions, currContext)
        : null;
    if (span) {
        trace.setSpan(ROOT_CONTEXT, span);
        propagation.inject(sessionContext, {}, defaultTextMapSetter);
    }
    const [spanner, setSpanner] = useState(span);
    const startNextOrEndSpan = useCallback((newSpan) => {
        propagation.extract(context.active(), carrier);
        if (newSpan && spanner) {
            const ctx = newSpan.spanContext();
            spanner.addLink({ context: ctx });
        }
        if (spanner) {
            spanner.end();
            flushWebTracedSpan(tracerName);
        }
        if (newSpan) {
            trace.setSpan(ROOT_CONTEXT, newSpan);
            sessionContext = context.active();
            propagation.inject(sessionContext, carrier, defaultTextMapSetter);
        }
        setSpanner(newSpan);
    }, [spanner, setSpanner]);
    useEffect(() => {
        function onUnmount() {
            if (spanner) {
                spanner.end();
            }
            flushWebTracedSpan(tracerName);
        }
        return () => onUnmount();
    }, []);
    return [spanner, startNextOrEndSpan];
}
export const SpanContext = createContext(null);
export function useSpan(tracerName) {
    const spanContext = useContext(SpanContext);
    if (!spanContext) {
        const error = new Error('useSpan must be used within an SpanContext provider');
        throw error;
    }
    const tracer = getWebTracer(tracerName);
    if (!tracer) {
        const error = new Error('useSpan depends on an OtelTracer');
        throw error;
    }
    const [activeSpan, setActiveSpan] = spanContext;
    useEffect(() => {
        function onUnmount() {
            if (activeSpan) {
                activeSpan.end();
            }
            flushWebTracedSpan(tracerName);
        }
        return () => onUnmount();
    }, [activeSpan]);
    return [activeSpan, setActiveSpan];
}
//# sourceMappingURL=useSpanContext.js.map